import { Badge, Box, Button, Group, Loader, Select, Stack, Text, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, AppUser } from '../api';
import { dayjs, isoDay, money } from '../format';
import { hasRole, useSession } from '../session';

interface LogRow {
  id: string;
  at: string;
  table_name: string;
  row_id: string | null;
  action: 'insert' | 'update' | 'delete';
  changed: Record<string, { old: unknown; new: unknown }> | null;
  row_before: Record<string, unknown> | null;
  row_after: Record<string, unknown> | null;
  actor: string | null;
  actor_source: string;
}

const TABLES: Record<string, string> = {
  'fin.cash_entry': 'Операции',
  'kpi.metric_value': 'Показатели',
  'kpi.plan_value': 'Планы',
  'fin.category': 'Статьи',
  'fin.category_alias': 'Соответствия статей',
  'kpi.metric': 'Справочник показателей',
  'kpi.metric_alias': 'Соответствия показателей',
  'org.department': 'Подразделения',
  'fin.allocation_rule': 'Правила деления',
  'fin.allocation_rule_share': 'Доли правил деления',
  'ops.sheet_source': 'Источники',
  'org.app_user': 'Пользователи',
  'org.user_role': 'Роли',
  'org.legal_entity': 'ООО',
  'org.branch': 'Филиалы',
  'org.login_tenant': 'Логины дашборда',
};
// порядок — порядок показа полей у добавленной или удалённой строки
const FIELDS: Record<string, string> = {
  code: 'код', legacy_name: 'в старой таблице', legacy_indicator: 'в старой таблице', name: 'название', full_name: 'имя', email: 'e-mail', amount: 'сумма', value: 'значение', op_date: 'дата', month: 'месяц',
  accrual_month: 'месяц начисления', category_id: 'статья', department_id: 'подразделение', legal_entity_id: 'ООО',
  description: 'назначение', comment: 'комментарий', payment_method: 'оплата', is_active: 'действует', deleted_at: 'удалено',
  needs_review: 'проверить', is_restricted: 'ФОТ', is_default: 'по умолчанию', must_change_password: 'временный пароль',
  role: 'роль', metric_id: 'показатель', is_ignored: 'не переносить', source: 'источник', share: 'доля', parent_id: 'группа',
  default_allocation_rule_id: 'правило деления', allocation_rule_id: 'правило деления', value_date: 'день',
  kind: 'вид', data_sheet: 'лист',
};
const HIDDEN = new Set(['id', 'tenant_id', 'created_at', 'updated_at', 'updated_by', 'created_by', 'import_run_id', 'source_key']);
const ACTIONS = { insert: ['добавлено', 'brand'], update: ['изменено', 'blue'], delete: ['удалено', 'red'] } as const;

function who(r: LogRow) {
  if (r.actor) return r.actor;
  const s = r.actor_source;
  if (s.startsWith('import:')) return `загрузка ${s.slice(7)}`;
  if (s.startsWith('template:')) return `шаблон ${s.slice(9)}`;
  if (s.startsWith('cli:')) return 'администратор (сервер)';
  if (s === 'sql') return 'вручную на сервере';
  return s === 'api' ? 'МИС' : s || 'сервер';
}

/** Журнал изменений: кто, когда и что поменял. */
export function AuditPage() {
  const { me, catalog } = useSession();
  const [range, setRange] = useState<[string | null, string | null]>([isoDay(dayjs().subtract(7, 'day').toDate()), isoDay(new Date())]);
  const [table, setTable] = useState<string | null>(null);
  const [user, setUser] = useState<string | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [rows, setRows] = useState<LogRow[] | null>(null);
  const [next, setNext] = useState<string | null>(null);

  useEffect(() => {
    if (hasRole(me, 'owner', 'admin')) api.get<{ users: AppUser[] }>('/users').then((r) => setUsers(r.users)).catch(() => undefined);
  }, [me]);

  const names = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of catalog?.categories ?? []) m.set(c.id, c.name);
    for (const d of catalog?.departments ?? []) m.set(d.id, d.name);
    for (const l of catalog?.legalEntities ?? []) m.set(l.id, l.name);
    for (const u of users) m.set(u.id, u.full_name);
    return m;
  }, [catalog, users]);

  const show = (k: string, v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'да' : 'нет';
    if (k === 'amount') return money(v as string);
    if (typeof v === 'string' && /^[0-9a-f-]{36}$/.test(v)) return names.get(v) ?? `…${v.slice(-6)}`;
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return dayjs(v).format('D MMM YYYY, HH:mm');
    return String(v);
  };

  const query = useCallback(
    (before?: string) => {
      const q = new URLSearchParams({ limit: '100' });
      if (range[0]) q.set('from', range[0]);
      if (range[1]) q.set('to', range[1]);
      if (table) q.set('table', table);
      if (user) q.set('user_id', user);
      if (before) q.set('before_id', before);
      return api.get<{ rows: LogRow[]; next: string | null }>(`/audit?${q}`);
    },
    [range, table, user],
  );

  useEffect(() => {
    setRows(null);
    query()
      .then((r) => {
        setRows(r.rows);
        setNext(r.next);
      })
      .catch((e) => notifications.show({ color: 'red', message: e.message }));
  }, [query]);

  const more = async () => {
    if (!next) return;
    const r = await query(next);
    setRows((x) => [...(x ?? []), ...r.rows]);
    setNext(r.next);
  };

  const details = (r: LogRow) => {
    if (r.action === 'update' && r.changed) {
      return Object.entries(r.changed)
        .filter(([k]) => !HIDDEN.has(k))
        .map(([k, v]) => `${FIELDS[k] ?? k}: ${show(k, v.old)} → ${show(k, v.new)}`);
    }
    const row = r.row_after ?? r.row_before ?? {};
    return Object.keys(FIELDS)
      .filter((k) => row[k] !== undefined && row[k] !== null && row[k] !== false)
      .slice(0, 6)
      .map((k) => `${FIELDS[k]}: ${show(k, row[k])}`);
  };

  return (
    <Box p={{ base: 'md', sm: 'xl' }} maw={1100}>
      <Title order={2} mb="md">Журнал изменений</Title>
      <Group gap="sm" mb="md" wrap="wrap">
        <DatePickerInput type="range" aria-label="Период" valueFormat="D MMM YYYY" value={range} onChange={(v) => setRange(v as [string | null, string | null])} w={{ base: '100%', sm: 260 }} />
        <Select aria-label="Что менялось" placeholder="Все таблицы" clearable data={Object.entries(TABLES).map(([value, label]) => ({ value, label }))} value={table} onChange={setTable} w={{ base: '100%', sm: 240 }} />
        {users.length > 0 && (
          <Select aria-label="Кто" placeholder="Все пользователи" clearable searchable data={users.map((u) => ({ value: u.id, label: u.full_name }))} value={user} onChange={setUser} w={{ base: '100%', sm: 240 }} />
        )}
      </Group>
      {hasRole(me, 'admin') && !hasRole(me, 'owner', 'finance') && (
        <Text size="sm" c="dimmed" mb="sm">Изменения операций, показателей и планов администратору не показываются.</Text>
      )}
      <Box className="mis-card" style={{ overflow: 'hidden' }}>
        {rows === null ? (
          <Group justify="center" p="xl"><Loader /></Group>
        ) : rows.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">За этот период изменений нет</Text>
        ) : (
          rows.map((r) => (
            <Group key={r.id} align="flex-start" wrap="nowrap" gap="md" px="md" py="sm" style={{ borderTop: '1px solid var(--mis-border)' }}>
              <Text size="sm" c="dimmed" w={110} style={{ flexShrink: 0 }}>{dayjs(r.at).format('D MMM, HH:mm')}</Text>
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Group gap={6}>
                  <Badge size="sm" variant="light" color={ACTIONS[r.action][1]}>{ACTIONS[r.action][0]}</Badge>
                  <Text size="sm" fw={500}>{TABLES[r.table_name] ?? r.table_name}</Text>
                  <Text size="sm" c="dimmed">· {who(r)}</Text>
                </Group>
                {details(r).map((d, i) => (
                  <Text key={i} size="sm" style={{ wordBreak: 'break-word' }}>{d}</Text>
                ))}
              </Stack>
            </Group>
          ))
        )}
      </Box>
      {next && (
        <Group justify="center" mt="md">
          <Button variant="default" onClick={() => void more().catch((e) => notifications.show({ color: 'red', message: e.message }))}>Показать ещё</Button>
        </Group>
      )}
    </Box>
  );
}
